import { OverlayViewF } from '@react-google-maps/api'
import { rem } from 'polished'
import React, { MouseEventHandler, useCallback, useContext, useEffect, useRef, useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import MapInfoWindow from './MapInfoWindow'
import PinHeart from './PinAccessory/PinHeart'
import SelectedMapItemPin from './Pins/SelectedMapItemPin'
import TeardropPin, { TEARDROP_PIN_HEIGHT } from './Pins/TeardropPin'
import MapSummaryCard from '../SummaryCards'
import {
  BaseMapElementComponentProps, pinOffSetter,
  startPlaceOfTripItem,
} from '../utils'
import { getPinColor, getItemNumber, scrollToItem } from './utils/tripItemMapUtil'
import useToggle from 'hooks/useToggle'

import Clickable from 'components/Common/Clickable'
import { ItemMetadata } from 'tripPlanner/models'
import { TripTab } from 'tripPlanner/utils/tabs'
import { MarkerItem } from 'tripPlanner/types/tripItem'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import { TripMapContext } from 'tripPlanner/contexts/TripMapContext'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import SolidHeartIcon from 'LuxKit/Icons/solid/SolidHeartIcon'
export const PIN_COLORS = {
  MONDAY: '#363A45',
  TUESDAY: '#02B990',
  WEDNESDAY: '#E49F4E',
  THURSDAY: '#8685E0',
  FRIDAY: '#BF9C88',
  SATURDAY: '#5DA6FF',
  SUNDAY: '#ff8ee8',
}

export const SELECTED_PIN_COLORS = {
  MONDAY: '#000000',
  TUESDAY: '#00604A',
  WEDNESDAY: '#986934',
  THURSDAY: '#494891',
  FRIDAY: '#5C5439',
  SATURDAY: '#304DAB',
  SUNDAY: '#A02332',
}

const StyledClickable = styled(Clickable)`
  position: absolute;
  left: 0;
  bottom: 0;
  transform: translateX(-50%);
`

const Container = styled.div`
  position: relative;
`

const PinHeartContainer = styled.div`
  position: absolute;
  bottom: ${rem(24)};
  left: ${rem(17)};
  z-index: 9999;
`

interface Props extends BaseMapElementComponentProps {
  item: MarkerItem
  enableWindow?: boolean
  tab: TripTab
  map?: google.maps.Map
}
const TARGET_ZOOM = 13
const TARGET_ZOOM_RANGE = 1.5
function TripItemMapMarker({
  active,
  item,
  position,
  zIndex,
  tab,
  map,
}: Props) {
  const theme = useTheme()
  const tripContext = useContext(TripContext)
  const { data: trip } = useTrip({ tripId: tripContext?.tripId })

  const pinColor = useMemo(() => getPinColor(item, trip, active), [item, trip, active])
  const number = useMemo(() => getItemNumber(item, trip), [item, trip])
  const windowRef = useRef<HTMLDivElement | null>(null)
  const { onSelectMapItem, onDeselectMapItem } = useContext(TripMapContext)

  const onClick: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (active) {
        onDeselectMapItem()
      } else {
        onSelectMapItem(tripItemSelectionId(item.id))
        scrollToItem(item)
      }
    },
    [active, onDeselectMapItem, onSelectMapItem, item],
  )

  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (ref.current && google?.maps) {
      google.maps.OverlayView.preventMapHitsFrom(ref.current)
    }
  }, [ref])

  useEffect(() => {
    if (active && position && map) {
      const bounds = map.getBounds()
      const currentZoom = map.getZoom()

      const needsMovement = !bounds?.contains(position) || (currentZoom && (currentZoom + TARGET_ZOOM_RANGE || currentZoom - TARGET_ZOOM_RANGE) < TARGET_ZOOM)
      const needsZoom = currentZoom && Math.abs(currentZoom - TARGET_ZOOM) > TARGET_ZOOM_RANGE

      if (needsMovement) {
        map.moveCamera({
          center: position,
          zoom: needsZoom ? TARGET_ZOOM : currentZoom,
        })
      }
    }
  }, [active, position, map])
  const [hovered, , onHover, offHover] = useToggle(false)

  const name = startPlaceOfTripItem(item)?.name ?? ''
  const icon = useMemo(() => {
    const validTypes = ['ACCOMMODATION', 'CAR_RENTAL']
    if (validTypes.includes(item.type)) {
      return ItemMetadata[item.type].icon
    }
    if (!item.startDate) {
      return SolidHeartIcon
    }
    return undefined
  }, [item.type, item.startDate])
  const overlayZIndex = active || hovered ? 1000 : zIndex

  return (
    <OverlayViewF
      zIndex={overlayZIndex}
      position={position}
      getPixelPositionOffset={pinOffSetter}
      mapPaneName="overlayMouseTarget"
    >
      <Container ref={ref} css={{ pointerEvents: 'none' }}>
        <StyledClickable
          onClick={onClick}
          onMouseEnter={onHover}
          onMouseLeave={offHover}
        >
          {tab === 'Explore' && !active && (
            <PinHeartContainer>
              <PinHeart />
            </PinHeartContainer>
          )}
          {!active && (
            <TeardropPin
              isActive={false}
              pinColour={pinColor}
              number={number}
              icon={icon}
              text={name}
            />
          )}
          {active && (
            <SelectedMapItemPin
              pinColour={pinColor}
              number={number}
              icon={icon}
              strokeColour={theme.palette.neutral.default.eight}
            />
          )}
        </StyledClickable>

        <MapInfoWindow
          ref={windowRef}
          open={active}
          alignment="center"
          position="above"
          style={{ top: -TEARDROP_PIN_HEIGHT * 1.3 }}
        >
          <MapSummaryCard item={item} />
        </MapInfoWindow>
      </Container>
    </OverlayViewF>
  )
}

export default React.memo(TripItemMapMarker)
